import React, {
  ChangeEvent,
  FormEvent,
  useEffect,
  useState,
  type FC,
} from "react";
import classes from "./ReviewModal.module.css";
import BackgroundMovieLayout from "./BackgroundMovieLayout";
import { convertMinutesToHoursAndMinutes } from "../Utils/helpers";
import { animate, motion, useAnimationControls } from "framer-motion";
import { useAppDispatch, useAppSelector } from "../Store/hooks";
import { toggleReviewModal } from "../Slices/reviewModalSlice";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import {
  usePostReviewMutation,
  useUploadPostMutation,
} from "../Slices/httpSlice";
import { toast } from "react-toastify";
import Spinner from "./Loaders/Spinner";
import { openViewReviewModal } from "../Slices/viewReviewModalSlice";
import BackgroundLayout from "./BackgroundLayout";
import { togglePostModal } from "../Slices/postModalSlice";
import PostTitlesModal from "./PostTitlesModal";
import {
  resetPostTitlesState,
  togglePostTitleModal,
} from "../Slices/postTitlesSlice";
import MovieCard from "./Drag-Cards/MovieCard";
import moment from "moment-timezone";

type PostModalProps = {
  fetchPosts:()=>void
};

const PostModal: FC<PostModalProps> = ({fetchPosts}) => {
  const [windowWidth, setWindowWidth] = useState<number>(0);

  useEffect(() => {
    const updateConstraints = () => {
      setWindowWidth(window.innerWidth);
    };

    updateConstraints();
    window.addEventListener("resize", updateConstraints);

    return () => {
      window.removeEventListener("resize", updateConstraints);
    };
  }, []);

  const controls = useAnimationControls();

  const [postReview, { isLoading }] = usePostReviewMutation();

  const variants = {
    hidden: {
      y: window.innerHeight,
      display: "none",
    },
    visible: {
      y: 0,
      display: "block",
    },
  };

  const { postModal } = useAppSelector((state) => state.postModalSlice);
  useEffect(() => {
    if (postModal) {
      setTitle("");
      setDescription("");
      controls.start("visible");
      dispatch(resetPostTitlesState());
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [postModal]);

  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.userSlice);
  const { selectedTitles } = useAppSelector((state) => state.postTitlesSlice);

  const closeReviewModal = () => {
    controls.start("hidden");
    dispatch(togglePostModal());
  };

  const [title, setTitle] = useState<string>("");
  const [description, setDescription] = useState<string>("");

  const [uploadPostApi, { isLoading: uploadPostLoading }] =
    useUploadPostMutation();

  const submitHandler = async () => {
    if (
      selectedTitles.length === 0 ||
      title.trim().length === 0 ||
      description.trim().length === 0
    ) {
      toast.error(
        "ကျေးဇူးပြု၍ Post အတွက် လိုအပ်သောအသေးစိတ်အချက်များအားလုံးကို ဖြည့်စွက်ပြီးဖြစ်ကြောင်း သေချာပါစေ။"
      );
      return;
    }
    const response: any = await uploadPostApi({
      body: {
        user_id: user?.id,
        headline: title,
        description: description,
        media: selectedTitles,
      },
      token: user?.token,
    });
    // console.log(response, "response....");

    if (response?.error) {
      console.log(response.error, "error");

      if (response?.error?.status === 403) {
        toast.error("ကျေးဇူးပြု၍ ထပ်မံဝင်ရောက်ပါ၊ တိုကင်သက်တမ်းကုန်ပါပြီ။");
      } else {
        toast.error(
          response?.error?.data?.error?.error ||
          response?.error?.data?.error ||
          response?.error?.data?.message ||
          response?.error?.message ||
          "Something Went Wrong!" 
        );
      }
    } else {
      setTitle("");
      setDescription("");
      dispatch(resetPostTitlesState());
      fetchPosts();
      closeReviewModal();
      toast.success("ပို့စ်ကို ကွန်မြူနတီတွင် မျှဝေပြီးပါပြီ။");
    }
  };

  const handleSelectorClick = () => {
    dispatch(togglePostTitleModal());
  };

  return (
    <motion.div
      className={classes.modal}
      variants={variants}
      initial="hidden"
      animate={controls}
      transition={{
        duration: 0.5,
        type: "spring",
        damping: 10,
        stiffness: 100,
      }}
    >
      <BackgroundLayout>
        <div className={classes.review_navbar}>
          <button
            className={classes.close_btn}
            type="button"
            onClick={closeReviewModal}
          >
            <i className={`pi pi-times ${classes.close_icon}`} />
          </button>
        </div>

        <div className={classes.category_title_container}>
          <div className={classes.title_container}>
            <p className={classes.category_title}>Post တစ်ခုထည့်ပါ။</p>
          </div>
        </div>

        <form className={classes.form_container}>
          <div className={classes.inputs}>
            <div className={classes.section_drag_cards}>
              <div className={classes.section_cards}>
                <motion.div
                  className={classes.section_card}
                  style={{
                    gridTemplateColumns: `repeat(${
                      selectedTitles.length + 1
                    }, 150px)`,
                  }}
                  drag="x"
                  dragConstraints={{
                    top: 0,
                    left: -(windowWidth <= 800
                      ? 10 +
                        (selectedTitles.length + 1) * 150 +
                        (selectedTitles.length + 1) * 10 +
                        (selectedTitles.length + 1) * 1 -
                        windowWidth
                      : window.innerWidth * 0.05 +
                        (selectedTitles.length + 1) * 150 +
                        (selectedTitles.length + 1) * 10 +
                        (selectedTitles.length + 1) * 1 -
                        windowWidth),
                    right: 0,
                    bottom: 0,
                  }}
                  transition={{
                    type: "spring",
                    damping: 100,
                    stiffness: 100,
                  }}
                >
                  <div
                    className={classes.media_selector}
                    onClick={handleSelectorClick}
                  >
                    <div className={classes.plus_icon}>
                      <i className={`pi pi-plus ${classes.plus}`} />
                    </div>
                    <p className={classes.media_selector_text}>
                    ခေါင်းစဉ် 1 မှ 6 ကြားကို ရွေးပါ။
                    </p>
                  </div>
                  {selectedTitles?.map((data, idx) => {
                    return (
                      <MovieCard
                        key={idx}
                        data={data}
                        type="selected titles and remove dispatch"
                      />
                    );
                  })}
                </motion.div>
              </div>
            </div>

            <InputText
              type="text"
              className={`p-inputtext-lg ${classes.input}`}
              placeholder="Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />

            <InputTextarea
              className={`p-inputtext-lg ${classes.input} ${classes.textarea}`}
              placeholder="Description"
              rows={6}
              cols={30}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              // onKeyDown={(e) => handleKeyDown(e)}
            />
          </div>

          <button
            type="button"
            disabled={uploadPostLoading}
            onClick={() => submitHandler()}
            className={classes.review_submit_btn}
          >
            {uploadPostLoading ? (
              <div
                style={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Spinner theme="light" />
              </div>
            ) : (
              "Submit"
            )}
          </button>
        </form>
      </BackgroundLayout>
      <PostTitlesModal />
    </motion.div>
  );
};

export default PostModal;
